import React, { useState, useEffect } from 'react';
import { createDirectusDemoRegistration } from '../utils/directusDemoRegistration';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../hooks/useLanguage';

/** Demo registration form — submits to Directus. */
export default function RegisterFormInner() {
  const { t } = useTranslation();
  const [isSpanish] = useLanguage();
  
  const [formData, setFormData] = useState({
    company: '',
    email: '',
    expectedDuas: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showContinueModal, setShowContinueModal] = useState(false);
  const [savedData, setSavedData] = useState(null);

  // Check sessionStorage on mount
  useEffect(() => {
    const savedEmail = sessionStorage.getItem('register_email');
    const savedCompany = sessionStorage.getItem('register_company');
    const savedExpectedDuas = sessionStorage.getItem('register_expectedDuas');

    if (savedEmail && savedCompany && savedExpectedDuas) {
      setSavedData({
        email: savedEmail,
        company: savedCompany,
        expectedDuas: savedExpectedDuas
      });
      setShowContinueModal(true);
    }
  }, []);

  const handleContinueWithSaved = () => {
    if (savedData) {
      setFormData({
        company: savedData.company,
        email: savedData.email,
        expectedDuas: savedData.expectedDuas
      });
    }
    setShowContinueModal(false);
  };

  const handleResetForm = () => {
    sessionStorage.removeItem('register_email');
    sessionStorage.removeItem('register_company');
    sessionStorage.removeItem('register_expectedDuas');
    sessionStorage.removeItem('register_success');
    setFormData({
      company: '',
      email: '',
      expectedDuas: ''
    });
    setShowContinueModal(false);
    setSavedData(null);
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'company':
        if (!value || (typeof value === 'string' && !value.trim())) {
          return t('register', 'form', 'errors', 'companyRequired');
        }
        break;
      case 'email':
        if (!value || (typeof value === 'string' && !value.trim())) {
          return t('register', 'form', 'errors', 'emailRequired');
        } else if (typeof value === 'string') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            return t('register', 'form', 'errors', 'validEmail');
          }
        }
        break;
      case 'expectedDuas':
        if (!value || (typeof value === 'string' && !value.trim()) || (typeof value === 'number' && value <= 0)) {
          return t('register', 'form', 'errors', 'expectedDuasRequired');
        }
        break;
    }
    return null;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'expectedDuas' ? parseInt(value) || '' : value
    }));
    
    // Clear field-specific errors when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    if (submitStatus) {
      setSubmitStatus(null);
      setSubmitMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('Form submitted with data:', formData);

    // Validate all fields
    const errors = {};
    Object.keys(formData).forEach(field => {
      const fieldError = validateField(field, formData[field]);
      if (fieldError) {
        errors[field] = fieldError;
      }
    });

    console.log('Validation errors:', errors);

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSubmitStatus('error');
      setSubmitMessage(t('register', 'form', 'errors', 'fillAllFields'));
      setTimeout(() => {
        setSubmitStatus(null);
        setSubmitMessage('');
      }, 10000);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage('');

    try {
      const expectedDuasValue =
        typeof formData.expectedDuas === 'string'
          ? parseInt(formData.expectedDuas, 10)
          : formData.expectedDuas;

      const result = await createDirectusDemoRegistration({
        company: formData.company,
        email: formData.email,
        expectedDuas: expectedDuasValue,
      });

      if (result.success === true) {
        // Store in sessionStorage on success
        sessionStorage.setItem('register_email', formData.email);
        sessionStorage.setItem('register_company', formData.company);
        sessionStorage.setItem('register_expectedDuas', typeof formData.expectedDuas === 'string' ? formData.expectedDuas : formData.expectedDuas.toString());
        sessionStorage.setItem('register_success', 'true');

        setSubmitStatus('success');
        setSubmitMessage(t('register', 'form', 'success'));
        setFormData({
          company: '',
          email: '',
          expectedDuas: ''
        });
        setFieldErrors({});
        
        // Redirect to demo page after successful submission with parameters
        setTimeout(() => {
          const params = new URLSearchParams({
            email: formData.email,
            company: formData.company,
            duas: typeof formData.expectedDuas === 'string' ? formData.expectedDuas : formData.expectedDuas.toString()
          });
          window.location.href = `/demo?${params.toString()}`;
        }, 2000);
      } else {
        throw new Error(result.error || 'Failed to send request');
      }
    } catch (error) {
      console.error('Registration form error:', error);
      setSubmitStatus('error');
      setSubmitMessage(
        error instanceof Error ? error.message : t('register', 'form', 'errors', 'generalError')
      );
      setTimeout(() => {
        setSubmitStatus(null);
        setSubmitMessage('');
      }, 10000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full  max-w-lg m-auto">
      {/* Continue Modal */}
      {showContinueModal && savedData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {isSpanish ? 'Continuar con información guardada' : 'Continue with saved information'}
            </h3>
            <p className="text-gray-600 mb-4">
              {isSpanish 
                ? 'Encontramos información guardada. ¿Desea continuar con estos datos?'
                : 'We found saved information. Would you like to continue with this data?'}
            </p>
            <div className="bg-gray-50 rounded-md p-4 mb-4">
              <p className="text-sm text-gray-700 mb-1">
                <span className="font-medium">{isSpanish ? 'Empresa:' : 'Company:'}</span> {savedData.company}
              </p>
              <p className="text-sm text-gray-700 mb-1">
                <span className="font-medium">{isSpanish ? 'Email:' : 'Email:'}</span> {savedData.email}
              </p>
              <p className="text-sm text-gray-700">
                <span className="font-medium">{isSpanish ? 'DUAs esperados:' : 'Expected DUAs:'}</span> {savedData.expectedDuas}
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleResetForm}
                className="py-2 px-4 rounded-full border-container hover:bg-gray-50 transition-colors"
              >
                {isSpanish ? 'No, empezar de nuevo' : 'No, start fresh'}
              </button>
              <button
                onClick={handleContinueWithSaved}
                className="py-2 px-4 rounded-full bg-default text-white font-medium hover:bg-opacity-90 transition-colors"
              >
                {isSpanish ? 'Sí, continuar' : 'Yes, continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Title */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 font-gdSage">
          {isSpanish ? 'Comienza con CargoFlow' : 'Get Started with CargoFlow'}
        </h2>
        <p className="text-gray-500 text-sm mt-1">
          {isSpanish 
            ? 'Por favor, complete el formulario a continuación para comenzar con CargoFlow.'
            : 'Please fill out the form below to get started with CargoFlow.'}
        </p>
      </div>

      {/* Company Information Form */}
      <form onSubmit={handleSubmit} className="bg-white mb-6">
        <div className=''>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
          {/* Company Name */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              {isSpanish ? 'Nombre de la Empresa' : 'Company Name'} *
            </label>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              placeholder={isSpanish ? 'Ingrese el nombre de su empresa' : 'Enter your company name'}
              className={`w-full px-4 py-2 border-container rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${fieldErrors.company ? "border-red-500" : ""}`}
              required
              disabled={isSubmitting}
            />
            {fieldErrors.company && (
              <p className="text-sm text-red-500 mt-1" role="alert">
                {fieldErrors.company}
              </p>
            )}
          </div>

          {/* Email Address */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              {isSpanish ? 'Dirección de Correo Electrónico' : 'Email Address'} *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={isSpanish ? 'Ingrese su dirección de correo electrónico' : 'Enter your email address'}
              className={`w-full px-4 py-2 border-container rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${fieldErrors.email ? "border-red-500" : ""}`}
              required
              disabled={isSubmitting}
            />
            {fieldErrors.email && (
              <p className="text-sm text-red-500 mt-1" role="alert">
                {fieldErrors.email}
              </p>
            )}
          </div>
        </div>

        {/* Expected Number of DUAs */}
        <div className="text-sm mt-8">
          <label className="block text-sm font-bold text-gray-700 mb-2">
            {isSpanish ? 'Número Esperado de DUAs por mes' : 'Expected Number of DUAs per month'} *
          </label>
          <input
            type="number"
            name="expectedDuas"
            value={formData.expectedDuas}
            onChange={handleInputChange}
            placeholder={isSpanish ? '¿Cuántas declaraciones DUA espera crear por mes?' : 'How many DUA declarations do you expect to create per month?'}
            className={`w-full px-4 py-2 border-container rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${fieldErrors.expectedDuas ? "border-red-500" : ""}`}
            required
            disabled={isSubmitting}
            min="1"
          />
          {fieldErrors.expectedDuas && (
            <p className="text-sm text-red-500 mt-1" role="alert">
              {fieldErrors.expectedDuas}
            </p>
          )}
          <p className="text-sm text-gray-500 mt-2">
            {isSpanish 
              ? 'Esto nos ayuda a comprender sus necesidades de uso y proporcionar mejores recomendaciones de servicio.'
              : 'This helps us understand your usage needs and provide better service recommendations.'}
          </p>
        </div>

        {/* Status Messages */}
        {submitStatus && (
          <div className={`mt-4 p-4 rounded-md ${submitStatus === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {submitMessage}
          </div>
        )}
        </div>

        {/* Continue Button */}
        <div className="flex w-full mt-10">
          <button
            type="submit"
            disabled={isSubmitting || (formData.company === '' || formData.email === '' || formData.expectedDuas === '')}
            className="template-button w-full py-3 px-6 rounded-full bg-default text-white font-bold hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting 
              ? (isSpanish ? 'Continuando...' : 'Continuing...') 
              : (isSpanish ? 'Continuar' : 'Continue')}
          </button>
        </div>

        
      </form>
    </div>
  );
}

